type TraceEntry = {
  id: number
  method: string
  url: string
  path: string
  search: string
  route: string
  started_at: string
  duration_ms: number | null
  status: number | null
  ok: boolean | null
  error: string | null
  stack: string[]
}

type ChattinessEndpoint = {
  request: string
  count: number
  total_ms: number
}

export type SupabaseChattinessWarning = {
  route: string
  request_count: number
  duration_ms: number
  repeated_endpoints: ChattinessEndpoint[]
  triggered_by: 'total' | 'repeated_endpoint'
  threshold: number
}

type SupabaseTraceApi = {
  entries: TraceEntry[]
  clear: () => void
  download: (format?: 'json' | 'csv') => void
  printSummary: () => void
}

declare global {
  interface Window {
    storytellerSupabaseTrace?: SupabaseTraceApi
  }
}

const MONITOR_STORAGE_KEY = 'storyteller:supabaseMonitor'
const TRACE_STORAGE_KEY = 'storyteller:supabaseTrace'
const SUPABASE_HOST_RE = /\.supabase\.co$/
const LOAD_IDLE_MS = 1200
const LOAD_MAX_MS = 8000
const TOTAL_REQUEST_WARNING_THRESHOLD = 30
const REPEATED_ENDPOINT_WARNING_THRESHOLD = 8

let installed = false
let sequence = 0
let traceEnabled = false

type LoadWindow = {
  route: string
  startedAt: number
  idleTimer: number | null
  maxTimer: number | null
  finalized: boolean
  entries: TraceEntry[]
}

let currentLoadWindow: LoadWindow | null = null

function shouldEnableMonitor(): boolean {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  if (params.get('supabaseMonitor') === '0') {
    window.localStorage?.setItem(MONITOR_STORAGE_KEY, '0')
    return false
  }
  if (params.get('supabaseMonitor') === '1') {
    window.localStorage?.removeItem(MONITOR_STORAGE_KEY)
    return true
  }
  return window.localStorage?.getItem(MONITOR_STORAGE_KEY) !== '0'
}

function shouldEnableDetailedTrace(): boolean {
  if (typeof window === 'undefined') return false
  const params = new URLSearchParams(window.location.search)
  if (params.get('supabaseTrace') === '1') {
    window.localStorage?.setItem(TRACE_STORAGE_KEY, '1')
    return true
  }
  if (params.get('supabaseTrace') === '0') {
    window.localStorage?.removeItem(TRACE_STORAGE_KEY)
    return false
  }
  return window.localStorage?.getItem(TRACE_STORAGE_KEY) === '1'
}

function requestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') return input
  if (input instanceof URL) return input.href
  return input.url
}

function requestMethod(input: RequestInfo | URL, init?: RequestInit): string {
  if (init?.method) return init.method.toUpperCase()
  if (typeof input === 'object' && 'method' in input && input.method) {
    return input.method.toUpperCase()
  }
  return 'GET'
}

function isSupabaseRequest(url: URL): boolean {
  return SUPABASE_HOST_RE.test(url.hostname)
}

function captureStack(): string[] {
  if (!traceEnabled) return []
  return (new Error().stack || '')
    .split('\n')
    .slice(2)
    .map((line) => line.trim())
    .filter((line) =>
      line
      && !line.includes('supabaseRequestTrace')
      && !line.includes('/node_modules/'),
    )
    .slice(0, 20)
}

function endpointKey(entry: TraceEntry): string {
  return `${entry.method} ${entry.path}`
}

function buildEndpointSummary(entries: TraceEntry[]): ChattinessEndpoint[] {
  const byEndpoint = new Map<string, ChattinessEndpoint>()
  for (const entry of entries) {
    const request = endpointKey(entry)
    const current = byEndpoint.get(request) || { request, count: 0, total_ms: 0 }
    current.count += 1
    current.total_ms += entry.duration_ms || 0
    byEndpoint.set(request, current)
  }
  return [...byEndpoint.values()]
    .map((endpoint) => ({ ...endpoint, total_ms: Math.round(endpoint.total_ms) }))
    .sort((a, b) => b.count - a.count || b.total_ms - a.total_ms)
}

function clearLoadWindowTimers(loadWindow: LoadWindow): void {
  if (loadWindow.idleTimer != null) {
    window.clearTimeout(loadWindow.idleTimer)
    loadWindow.idleTimer = null
  }
  if (loadWindow.maxTimer != null) {
    window.clearTimeout(loadWindow.maxTimer)
    loadWindow.maxTimer = null
  }
}

function finalizeLoadWindow(loadWindow: LoadWindow): void {
  if (loadWindow.finalized) return
  loadWindow.finalized = true
  clearLoadWindowTimers(loadWindow)

  const endpointSummary = buildEndpointSummary(loadWindow.entries)
  const repeatedEndpoint = endpointSummary.find((endpoint) =>
    endpoint.count >= REPEATED_ENDPOINT_WARNING_THRESHOLD,
  )
  const totalTriggered = loadWindow.entries.length >= TOTAL_REQUEST_WARNING_THRESHOLD
  if (!totalTriggered && !repeatedEndpoint) return

  const detail: SupabaseChattinessWarning = {
    route: loadWindow.route,
    request_count: loadWindow.entries.length,
    duration_ms: Math.round(performance.now() - loadWindow.startedAt),
    repeated_endpoints: endpointSummary.filter((endpoint) =>
      endpoint.count >= REPEATED_ENDPOINT_WARNING_THRESHOLD,
    ),
    triggered_by: totalTriggered ? 'total' : 'repeated_endpoint',
    threshold: totalTriggered
      ? TOTAL_REQUEST_WARNING_THRESHOLD
      : REPEATED_ENDPOINT_WARNING_THRESHOLD,
  }

  window.dispatchEvent(new CustomEvent<SupabaseChattinessWarning>(
    'storyteller:supabase-chattiness',
    { detail },
  ))
}

function getLoadWindowForCurrentRoute(): LoadWindow {
  const route = window.location.href
  if (currentLoadWindow && currentLoadWindow.route === route && !currentLoadWindow.finalized) {
    return currentLoadWindow
  }

  if (currentLoadWindow && !currentLoadWindow.finalized) {
    finalizeLoadWindow(currentLoadWindow)
  }

  currentLoadWindow = {
    route,
    startedAt: performance.now(),
    idleTimer: null,
    maxTimer: null,
    finalized: false,
    entries: [],
  }
  const loadWindow = currentLoadWindow
  loadWindow.maxTimer = window.setTimeout(() => {
    finalizeLoadWindow(loadWindow)
  }, LOAD_MAX_MS)
  return loadWindow
}

function trackLoadWindowEntry(entry: TraceEntry): void {
  const loadWindow = getLoadWindowForCurrentRoute()
  loadWindow.entries.push(entry)
  if (loadWindow.idleTimer != null) {
    window.clearTimeout(loadWindow.idleTimer)
  }
  loadWindow.idleTimer = window.setTimeout(() => {
    finalizeLoadWindow(loadWindow)
  }, LOAD_IDLE_MS)
}

function toCsvValue(value: unknown): string {
  const text = Array.isArray(value) ? value.join(' | ') : String(value ?? '')
  return `"${text.replaceAll('"', '""')}"`
}

function downloadText(filename: string, text: string, type: string): void {
  const blob = new Blob([text], { type })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

function createApi(entries: TraceEntry[]): SupabaseTraceApi {
  return {
    entries,
    clear() {
      entries.length = 0
      sequence = 0
      if (currentLoadWindow) {
        clearLoadWindowTimers(currentLoadWindow)
        currentLoadWindow = null
      }
    },
    download(format = 'json') {
      const stamp = new Date().toISOString().replace(/[:.]/g, '-')
      if (format === 'csv') {
        const rows = [
          ['id', 'method', 'status', 'duration_ms', 'path', 'search', 'route', 'stack'],
          ...entries.map((entry) => [
            entry.id,
            entry.method,
            entry.status,
            entry.duration_ms,
            entry.path,
            entry.search,
            entry.route,
            entry.stack,
          ]),
        ]
        const csv = rows.map((row) => row.map(toCsvValue).join(',')).join('\n')
        downloadText(`supabase-trace-${stamp}.csv`, csv, 'text/csv')
        return
      }
      downloadText(
        `supabase-trace-${stamp}.json`,
        JSON.stringify(entries, null, 2),
        'application/json',
      )
    },
    printSummary() {
      const byPath = new Map<string, { count: number; totalMs: number }>()
      for (const entry of entries) {
        const key = `${entry.method} ${entry.path}`
        const current = byPath.get(key) || { count: 0, totalMs: 0 }
        current.count += 1
        current.totalMs += entry.duration_ms || 0
        byPath.set(key, current)
      }
      console.table(
        [...byPath.entries()]
          .map(([request, value]) => ({
            request,
            count: value.count,
            total_ms: Math.round(value.totalMs),
          }))
          .sort((a, b) => b.count - a.count || b.total_ms - a.total_ms),
      )
    },
  }
}

export function installSupabaseRequestTrace(): SupabaseTraceApi | null {
  if (installed || !shouldEnableMonitor() || typeof window.fetch !== 'function') {
    return window.storytellerSupabaseTrace || null
  }

  installed = true
  traceEnabled = shouldEnableDetailedTrace()
  const entries: TraceEntry[] = []
  const originalFetch = window.fetch.bind(window)
  const api = createApi(entries)
  window.storytellerSupabaseTrace = api

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const rawUrl = requestUrl(input)
    let parsedUrl: URL
    try {
      parsedUrl = new URL(rawUrl, window.location.origin)
    } catch {
      return originalFetch(input, init)
    }

    if (!isSupabaseRequest(parsedUrl)) {
      return originalFetch(input, init)
    }

    const started = performance.now()
    const entry: TraceEntry = {
      id: ++sequence,
      method: requestMethod(input, init),
      url: parsedUrl.href,
      path: parsedUrl.pathname,
      search: parsedUrl.search,
      route: window.location.href,
      started_at: new Date().toISOString(),
      duration_ms: null,
      status: null,
      ok: null,
      error: null,
      stack: captureStack(),
    }
    entries.push(entry)
    trackLoadWindowEntry(entry)

    try {
      const response = await originalFetch(input, init)
      entry.duration_ms = Math.round(performance.now() - started)
      entry.status = response.status
      entry.ok = response.ok
      return response
    } catch (error) {
      entry.duration_ms = Math.round(performance.now() - started)
      entry.error = error instanceof Error ? error.message : String(error)
      throw error
    }
  }

  if (traceEnabled) {
    console.info(
      '[supabaseTrace] Recording Supabase requests with stack frames. Use storytellerSupabaseTrace.download() or storytellerSupabaseTrace.printSummary(). Disable stacks with ?supabaseTrace=0.',
    )
  }

  return api
}

installSupabaseRequestTrace()

export {}
